// Hover for a HollowScript editor: the symbol under the cursor's kind and,
// where available, its declared type and documentation. Two things this
// deliberately does *not* attempt, both explained where they matter:
//
//   - Inferred types. `@hollow/script`'s checker-level type inference is not
//     part of the published package (`scope.ts`'s own doc comment covers
//     this in full) - only a *declared* annotation (`scope.ts`'s
//     `typeText`) is ever shown for a local binding.
//   - Precise receiver-type resolution for a member access (`foo.bar`).
//     Without inference, `foo`'s real type isn't known, so a member hover
//     falls back to "every stdlib member with this name" when `foo` isn't a
//     recognised capability namespace - the same honest ambiguity
//     `stdlib.ts`'s `stdlibMembersNamed` already documents.
//
// Both are the real message-catalogue-style documentation the task brief
// points at (`stdlib.md`, folded into `stdlib.ts`) plus this project's own
// real capability surface (`capabilityCatalog.ts`) - never invented text.

import { parse } from '@hollow/script';
import type { StudioCapabilityName } from '../capabilities.js';
import { capabilityMembers, capabilityNamespaceDetail, grantedCapabilityNames, HOLLOWSCRIPT_CAPABILITY_CATALOG } from './capabilityCatalog.js';
import { collectScopeAt } from './scope.js';
import { HOLLOWSCRIPT_KEYWORDS, STDLIB_FREE_FUNCTIONS, stdlibMemberName, stdlibMembersNamed } from './stdlib.js';

export interface HollowScriptHoverParams {
  readonly source: string;
  readonly offset: number;
  /** The identifier word under the cursor (Monaco's own
   * `model.getWordAtPosition`, already handles word-boundary detection -
   * this module takes the result rather than re-deriving it). Empty/absent
   * means "no word under the cursor", not an error. */
  readonly word: string;
  /** Set when `word` is immediately preceded by `<memberOf>.` on the same
   * line - i.e. the cursor is over the property side of a member access. */
  readonly memberOf: string | undefined;
  readonly grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined;
}

export interface HollowScriptHoverInfo {
  /** Rendered as separate Markdown blocks by the Monaco adapter (one string
   * per line of the hover's contents array) - kept as plain strings here so
   * this module carries no `monaco-editor` dependency (see `tokenize.ts`'s
   * own doc comment). */
  readonly markdownLines: readonly string[];
}

function capitalize(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

function hoverForMember(memberOf: string, word: string, granted: ReadonlySet<StudioCapabilityName> | undefined): HollowScriptHoverInfo | null {
  if ((grantedCapabilityNames(granted) as readonly string[]).includes(memberOf)) {
    const found = capabilityMembers(memberOf).find((member) => member.name === word);
    if (found) return { markdownLines: [`\`${found.signature}\``, `Capability method - requires \`uses ${memberOf}\`.`] };
  } else if (HOLLOWSCRIPT_CAPABILITY_CATALOG.has(memberOf)) {
    // A real capability, just not granted to this project/session right now.
    const found = capabilityMembers(memberOf).find((member) => member.name === word);
    if (found) return { markdownLines: [`\`${found.signature}\``, `Capability method - **not granted**. Add \`${memberOf}\` to this project's scripting capabilities, or add \`uses ${memberOf}\`.`] };
  }
  const stdlibMatches = stdlibMembersNamed(word);
  if (stdlibMatches.length > 0) {
    return { markdownLines: stdlibMatches.map((member) => `\`${member.receiver}.${member.signature}\`  \n${member.doc}`) };
  }
  return null;
}

/** Hover info for the symbol at `params.offset`, or `null` when there's
 * nothing to say about it. Never throws: `parse` itself never does
 * (spec.md gate 2). */
export function computeHollowScriptHover(params: HollowScriptHoverParams): HollowScriptHoverInfo | null {
  const { word, memberOf, grantedCapabilities: granted } = params;
  if (word === '') return null;

  if (memberOf !== undefined) return hoverForMember(memberOf, word, granted);

  if ((HOLLOWSCRIPT_KEYWORDS as readonly string[]).includes(word)) {
    return { markdownLines: [`\`${word}\` - HollowScript keyword.`] };
  }

  if (HOLLOWSCRIPT_CAPABILITY_CATALOG.has(word)) {
    const isGranted = (grantedCapabilityNames(granted) as readonly string[]).includes(word);
    const detail = capabilityNamespaceDetail(word) ?? 'capability';
    return {
      markdownLines: [
        `\`${word}\` - ${detail}`,
        isGranted ? 'Granted to this project.' : `Not granted to this project. Add it to Game Setup's scripting capabilities, or add \`uses ${word}\`.`,
      ],
    };
  }

  const freeMatch = STDLIB_FREE_FUNCTIONS.find((fn) => stdlibMemberName(fn) === word);
  if (freeMatch) return { markdownLines: [`\`${freeMatch.signature}\``, freeMatch.doc] };

  const { program } = parse(params.source);
  const matches = collectScopeAt(program, params.offset).filter((binding) => binding.name === word);
  if (matches.length > 0) {
    // Prefer the last match: traversal order pushes an enclosing scope's own
    // binding before descending into the branch containing the offset, so
    // the innermost/shadowing declaration (the one actually in effect at
    // the cursor) is the last one collected.
    const binding = matches[matches.length - 1]!;
    return {
      markdownLines: [`\`${binding.name}\`: ${binding.typeText ?? '?'}`, capitalize(binding.kind) + (binding.kind === 'import' ? ' (from an `import`).' : '.')],
    };
  }

  return null;
}
